// DSC35-S5 (docs/build-briefs/DSC35-S5-editor.md; DEC-257, DEC-258, DEC-260):
// the Schedule and Applies-to editor that opens inside a discount's row in
// Settings > Discounts.
//
// Two halves. The top is PURE draft logic (read a draft from a discount, say
// what is wrong with it in a plain sentence, turn it into the PATCH body, word
// it through the ONE shared summarizer). The bottom is one DOM builder. Kept
// out of admin.ts, already very large, so admin.ts only wires it in.
//
// Every control is a switch, chip, date control or fixed list: nothing is
// typed (CLAUDE.md "a value from a fixed vocabulary or format is picked, never
// typed"). The discount's name and amount stay the row's own fields.
//
// Nothing here applies or prices a discount. It edits what S1 stores (the
// schedule) and S2 stores (what a special covers), through the existing PATCH.

import {
  DAY_SHORT,
  formatClockTime,
  summarizeSchedule,
} from "../../src/domain/discountScheduleSummary.ts";
import { describeSetting } from "./settingsDescription.ts";
import { sortByName } from "./nameSort.ts";
import { isCurrentItemKind, type DiscountDto, type DiscountScheduleDto, type DiscountSpecialScopeEntryDto } from "./api.ts";

/** The plan's ship note: the "Applies itself" copy must not read as a promise
 * until S3 (auto-apply at ring time) is live. The slice's task asks for the
 * note, so it shows; set this false before S5 merges ahead of S3. */
export const SCHEDULED_SPECIALS_APPLY_ITSELF_LIVE = true;

export const ALL_DAYS: readonly number[] = [1, 2, 3, 4, 5, 6, 7];
export const WEEKDAYS: readonly number[] = [1, 2, 3, 4, 5];

type ScopeEntry = { kind: DiscountSpecialScopeEntryDto["kind"]; value: string; name?: string };

export interface SpecialEditorDraft {
  scheduled: boolean;
  weekdays: number[];
  dateOn: boolean;
  dateFrom: string;
  dateTo: string;
  repeatYearly: boolean;
  timeOn: boolean;
  timeFrom: string;
  timeTo: string;
  scope: ScopeEntry[];
}

const DEFAULT_TIME_FROM = "16:00";
const DEFAULT_TIME_TO = "18:00";

/** "16:00:00" as "16:00". */
function clock(time: string | null, fallback: string): string {
  return time === null ? fallback : time.slice(0, 5);
}

export function draftFromDiscount(discount: DiscountDto): SpecialEditorDraft {
  const schedule = discount.schedule ?? null;
  return {
    scheduled: schedule !== null,
    weekdays: schedule === null ? [...ALL_DAYS] : [...schedule.weekdays].sort((a, b) => a - b),
    dateOn: schedule !== null && schedule.dateFrom !== null,
    dateFrom: schedule?.dateFrom ?? "",
    dateTo: schedule?.dateTo ?? "",
    repeatYearly: schedule?.repeatYearly ?? false,
    timeOn: schedule !== null && schedule.timeFrom !== null,
    timeFrom: clock(schedule?.timeFrom ?? null, DEFAULT_TIME_FROM),
    timeTo: clock(schedule?.timeTo ?? null, DEFAULT_TIME_TO),
    scope: (discount.specialScope ?? []).map((entry) => ({ ...entry })),
  };
}

/** The schedule the draft describes, or null when it is not scheduled. */
export function draftSchedule(draft: SpecialEditorDraft): DiscountScheduleDto | null {
  if (!draft.scheduled) {
    return null;
  }
  return {
    weekdays: [...draft.weekdays].sort((a, b) => a - b),
    dateFrom: draft.dateOn && draft.dateFrom !== "" ? draft.dateFrom : null,
    dateTo: draft.dateOn && draft.dateTo !== "" ? draft.dateTo : null,
    repeatYearly: draft.dateOn && draft.repeatYearly,
    timeFrom: draft.timeOn ? draft.timeFrom : null,
    timeTo: draft.timeOn ? draft.timeTo : null,
  };
}

/** What Save sends. Off sends schedule null and scope null: the discount
 * returns to the staff-applied one it was, with nothing left hidden. */
export function draftToPayload(draft: SpecialEditorDraft): {
  schedule: DiscountScheduleDto | null;
  specialScope: Array<{ kind: ScopeEntry["kind"]; value: string }> | null;
} {
  const schedule = draftSchedule(draft);
  return {
    schedule,
    specialScope:
      schedule === null
        ? null
        : draft.scope.map((entry) => ({ kind: entry.kind, value: entry.value })),
  };
}

/** A plain sentence for what is wrong, or null. The server still judges
 * everything; this only saves a round trip for the obvious mistakes. */
export function draftProblem(draft: SpecialEditorDraft): string | null {
  if (draft.scope.some((entry) => entry.kind === "item_kind" && !isCurrentItemKind(entry.value)))
    return "This discount covers a removed kind. Owner mapping is required before its scope can change.";
  if (!draft.scheduled) {
    return null;
  }
  if (draft.weekdays.length === 0) {
    return "Pick at least one day.";
  }
  if (draft.dateOn && (draft.dateFrom === "" || draft.dateTo === "")) {
    return "Pick both dates, or turn the date range off.";
  }
  if (draft.dateOn && !draft.repeatYearly && draft.dateFrom > draft.dateTo) {
    return "The first date is after the last date. Turn on Repeat yearly for a range that crosses New Year.";
  }
  if (draft.timeOn && draft.timeFrom === draft.timeTo) {
    return "The two times must differ. Turn the time range off to run all day.";
  }
  return null;
}

/** The schedule in words, from the ONE shared summarizer. */
export function draftSummary(draft: SpecialEditorDraft): string {
  const schedule = draftSchedule(draft);
  if (schedule === null) {
    return "Staff apply this discount themselves, any time.";
  }
  if (schedule.weekdays.length === 0) {
    return "Pick at least one day.";
  }
  // A half-filled date range reads as no date range until both are picked.
  return summarizeSchedule(
    schedule.dateFrom === null || schedule.dateTo === null
      ? { ...schedule, dateFrom: null, dateTo: null, repeatYearly: false }
      : schedule,
  );
}

/** The fixed 15-minute vocabulary: "00:00" through "23:45". */
export function timeChoices(): string[] {
  const out: string[] = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += 15) {
    const h = String(Math.floor(minutes / 60)).padStart(2, "0");
    const m = String(minutes % 60).padStart(2, "0");
    out.push(`${h}:${m}`);
  }
  return out;
}

export function timeLabel(time: string): string {
  return time === "00:00" ? "Midnight (12:00 AM)" : formatClockTime(time);
}

/** "Eastern Time" for "America/New_York", from the browser's own zone data.
 * Falls back to the IANA id only when Intl gives nothing. */
export function zoneFriendlyName(zone: string): string {
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "longGeneric" })
      .formatToParts(new Date())
      .find((p) => p.type === "timeZoneName");
    if (part !== undefined && part.value.trim() !== "") {
      return part.value;
    }
  } catch (error) {
    if (!(error instanceof RangeError)) {
      throw error;
    }
  }
  return zone;
}

/** True while the location's zone is the unset default. */
export function zoneIsUnset(zone: string): boolean {
  return zone.trim() === "" || zone === "UTC";
}

export interface PickableNamed {
  id: string;
  name: string;
}

export type PickableLoad =
  { ok: true; drinks: PickableNamed[]; bottles: PickableNamed[] } | { ok: false; message: string };

export interface SpecialEditorOptions {
  discount: DiscountDto;
  draft: SpecialEditorDraft;
  /** Read at the moment of need, not captured, so a zone saved on another
   * screen is honoured. */
  getZone: () => string;
  /** Item kinds (Beer, Wine, ...) and spirit categories, by label. */
  kindOptions: ReadonlyArray<{ value: string; label: string }>;
  categoryOptions: ReadonlyArray<{ value: string; label: string }>;
  loadPickable: () => Promise<PickableLoad>;
  onGoToZone: () => void;
  onSave: (
    payload: ReturnType<typeof draftToPayload>,
  ) => Promise<{ ok: true } | { ok: false; message: string }>;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) {
    node.className = className;
  }
  if (text !== undefined) {
    node.textContent = text;
  }
  return node;
}

/** UX37 (DEC-184): help ids must be unique while two editors can be on the
 * page, so each help line gets a serial. */
let helpSerial = 0;
function nextHelpId(base: string): string {
  helpSerial += 1;
  return `${base}-help-${helpSerial}`;
}

function tid<T extends HTMLElement>(node: T, testid: string): T {
  node.dataset["testid"] = testid;
  return node;
}

export function renderSpecialEditor(options: SpecialEditorOptions): HTMLElement {
  const { discount, draft } = options;
  const syncs: Array<() => void> = [];
  const syncAll = (): void => {
    for (const sync of syncs) {
      sync();
    }
  };

  const root = tid(el("section", "admin-special"), "admin-discount-special");
  root.setAttribute("aria-label", `Schedule and coverage for ${discount.name}`);

  // --- Summary first, so the card reads before it is edited. -------------
  const summaryBox = el("div", "admin-special-summary");
  summaryBox.append(el("span", "admin-special-summary-label", "Runs"));
  const summaryText = tid(
    el("strong", "admin-special-summary-text"),
    "admin-discount-special-summary",
  );
  summaryBox.append(summaryText);
  syncs.push(() => {
    summaryText.textContent = draftSummary(draft);
  });
  root.append(summaryBox);

  // --- Run on a schedule switch. -----------------------------------------
  const switchLabel = el("label", "admin-toggle-label admin-special-switch");
  const switchInput = tid(el("input", "admin-toggle-input"), "admin-discount-special-scheduled");
  switchInput.type = "checkbox";
  switchInput.setAttribute("role", "switch");
  switchInput.setAttribute("aria-label", `Run ${discount.name} on a schedule`);
  switchLabel.append(
    switchInput,
    el("span", "admin-toggle-track"),
    el("span", "admin-special-switch-text", "Run on a schedule"),
  );
  const switchHelp = el(
    "p",
    "admin-special-help",
    "Off means staff pick this discount themselves, any time.",
  );
  const switchHelpId = nextHelpId("admin-discount-special-scheduled");
  switchHelp.id = switchHelpId;
  describeSetting(switchInput, switchHelpId);
  const switchGroup = el("div", "admin-special-switch-group");
  switchGroup.append(switchLabel, switchHelp);
  root.append(switchGroup);

  const scheduleBody = el("div", "admin-special-body");
  root.append(scheduleBody);

  // --- Time zone line and the unset-zone warning. ------------------------
  const zoneLine = tid(el("p", "admin-special-zone"), "admin-discount-special-zone");
  const zoneWarning = tid(el("p", "admin-special-warning"), "admin-discount-special-zone-warning");
  zoneWarning.setAttribute("role", "alert");
  const zoneLink = tid(
    el("button", "admin-special-link", "time zone"),
    "admin-discount-special-zone-link",
  );
  zoneLink.type = "button";
  zoneLink.addEventListener("click", () => options.onGoToZone());
  zoneWarning.append(
    document.createTextNode("Times run in your location's "),
    zoneLink,
    document.createTextNode(", and it is still the default time zone. Set it first, then save."),
  );
  syncs.push(() => {
    const zone = options.getZone();
    const unset = zoneIsUnset(zone);
    zoneLine.textContent = unset
      ? "Times are in UTC until a time zone is set."
      : `Times are ${zoneFriendlyName(zone)}.`;
    zoneWarning.hidden = !unset;
  });
  scheduleBody.append(zoneLine, zoneWarning);

  // --- Days. --------------------------------------------------------------
  const daysGroup = el("div", "admin-special-group");
  daysGroup.append(el("span", "admin-special-group-title", "Days"));
  const dayRow = el("div", "admin-special-chips");
  dayRow.setAttribute("role", "group");
  dayRow.setAttribute("aria-label", "Days of the week");
  const sameDays = (a: readonly number[], b: readonly number[]): boolean =>
    a.length === b.length && [...a].sort().join() === [...b].sort().join();
  const makeChip = (label: string, testid: string): HTMLButtonElement => {
    const chip = tid(el("button", "admin-special-chip", label), testid);
    chip.type = "button";
    return chip;
  };
  const shortcut = (label: string, key: string, days: readonly number[]): void => {
    const chip = makeChip(label, "admin-discount-special-day-shortcut");
    chip.dataset["shortcut"] = key;
    chip.addEventListener("click", () => {
      draft.weekdays = [...days];
      syncAll();
    });
    syncs.push(() => {
      setPressed(chip, sameDays(draft.weekdays, days), label);
    });
    dayRow.append(chip);
  };
  shortcut("All days", "all", ALL_DAYS);
  shortcut("Weekdays", "weekdays", WEEKDAYS);
  DAY_SHORT.forEach((short, index) => {
    const day = index + 1;
    const chip = makeChip(short, "admin-discount-special-day");
    chip.dataset["day"] = String(day);
    chip.addEventListener("click", () => {
      draft.weekdays = draft.weekdays.includes(day)
        ? draft.weekdays.filter((d) => d !== day)
        : [...draft.weekdays, day].sort((a, b) => a - b);
      syncAll();
    });
    syncs.push(() => setPressed(chip, draft.weekdays.includes(day), short));
    dayRow.append(chip);
  });
  daysGroup.append(dayRow);
  scheduleBody.append(daysGroup);

  // --- Date range. --------------------------------------------------------
  const dateGroup = el("div", "admin-special-group");
  const dateSwitch = makeSwitch("Date range", "admin-discount-special-date-on");
  dateGroup.append(dateSwitch.label);
  const dateBody = el("div", "admin-special-pair");
  const dateFrom = makeDate("From", "admin-discount-special-date-from");
  const dateTo = makeDate("To", "admin-discount-special-date-to");
  dateBody.append(dateFrom.wrap, dateTo.wrap);
  const yearlyLabel = el("label", "admin-discount-check admin-special-yearly");
  const yearly = tid(el("input"), "admin-discount-special-repeat-yearly");
  yearly.type = "checkbox";
  const yearlyHelp = el(
    "p",
    "admin-special-help",
    "Uses the same dates every year, so a range can cross New Year.",
  );
  const yearlyHelpId = nextHelpId("admin-discount-special-repeat-yearly");
  yearlyHelp.id = yearlyHelpId;
  describeSetting(yearly, yearlyHelpId);
  yearlyLabel.append(yearly, document.createTextNode(" Repeat yearly"));
  dateBody.append(yearlyLabel, yearlyHelp);
  dateGroup.append(dateBody);
  scheduleBody.append(dateGroup);
  dateSwitch.input.addEventListener("change", () => {
    draft.dateOn = dateSwitch.input.checked;
    syncAll();
  });
  dateFrom.input.addEventListener("change", () => {
    draft.dateFrom = dateFrom.input.value;
    syncAll();
  });
  dateTo.input.addEventListener("change", () => {
    draft.dateTo = dateTo.input.value;
    syncAll();
  });
  yearly.addEventListener("change", () => {
    draft.repeatYearly = yearly.checked;
    syncAll();
  });
  syncs.push(() => {
    dateSwitch.input.checked = draft.dateOn;
    dateFrom.input.value = draft.dateFrom;
    dateTo.input.value = draft.dateTo;
    yearly.checked = draft.repeatYearly;
    for (const control of [dateFrom.input, dateTo.input, yearly]) {
      control.disabled = !draft.dateOn;
    }
    dateBody.classList.toggle("admin-special-off", !draft.dateOn);
  });

  // --- Time range. --------------------------------------------------------
  const timeGroup = el("div", "admin-special-group");
  const timeSwitch = makeSwitch("Time range", "admin-discount-special-time-on");
  timeGroup.append(timeSwitch.label);
  const timeBody = el("div", "admin-special-pair");
  const timeFrom = makeTimeSelect("From", "admin-discount-special-time-from");
  const timeTo = makeTimeSelect("To", "admin-discount-special-time-to");
  timeBody.append(timeFrom.wrap, timeTo.wrap);
  timeGroup.append(timeBody);
  scheduleBody.append(timeGroup);
  timeSwitch.input.addEventListener("change", () => {
    draft.timeOn = timeSwitch.input.checked;
    syncAll();
  });
  timeFrom.select.addEventListener("change", () => {
    draft.timeFrom = timeFrom.select.value;
    syncAll();
  });
  timeTo.select.addEventListener("change", () => {
    draft.timeTo = timeTo.select.value;
    syncAll();
  });
  syncs.push(() => {
    timeSwitch.input.checked = draft.timeOn;
    timeFrom.setValue(draft.timeFrom);
    timeTo.setValue(draft.timeTo);
    timeFrom.select.disabled = !draft.timeOn;
    timeTo.select.disabled = !draft.timeOn;
    timeBody.classList.toggle("admin-special-off", !draft.timeOn);
  });

  // --- Applies to. --------------------------------------------------------
  const applies = el("div", "admin-special-applies");
  applies.append(el("h4", "admin-special-heading", "Applies to"));
  const coversBox = tid(el("div", "admin-special-covers"), "admin-discount-special-covers");
  coversBox.append(el("span", "admin-special-group-title", "This discount covers"));
  const coversRow = el("div", "admin-special-chips");
  coversBox.append(coversRow);
  const coversEmpty = el(
    "p",
    "admin-special-help",
    "Nothing yet. Pick a category or a drink below.",
  );
  coversBox.append(coversEmpty);
  applies.append(coversBox);

  const entryKey = (entry: { kind: string; value: string }): string =>
    `${entry.kind}:${entry.value}`;
  const hasEntry = (kind: ScopeEntry["kind"], value: string): boolean =>
    draft.scope.some((entry) => entry.kind === kind && entry.value === value);
  const toggleEntry = (entry: ScopeEntry): void => {
    draft.scope = hasEntry(entry.kind, entry.value)
      ? draft.scope.filter((candidate) => entryKey(candidate) !== entryKey(entry))
      : [...draft.scope, entry];
    syncAll();
  };
  const labelFor = (entry: ScopeEntry): string => {
    if (entry.kind === "item_kind") {
      return options.kindOptions.find((o) => o.value === entry.value)?.label ?? entry.value;
    }
    if (entry.kind === "category") {
      return options.categoryOptions.find((o) => o.value === entry.value)?.label ?? entry.value;
    }
    return entry.name ?? "A drink no longer on the menu";
  };

  syncs.push(() => {
    coversRow.replaceChildren();
    for (const entry of draft.scope) {
      const label = labelFor(entry);
      const chip = tid(
        el("span", "admin-chip admin-special-cover-chip"),
        "admin-discount-special-covers-chip",
      );
      chip.dataset["kind"] = entry.kind;
      chip.dataset["value"] = entry.value;
      chip.append(document.createTextNode(label));
      const remove = tid(
        el("button", "admin-chip-remove", "×"),
        "admin-discount-special-covers-remove",
      );
      remove.type = "button";
      remove.setAttribute("aria-label", `Remove ${label}`);
      remove.addEventListener("click", () => toggleEntry(entry));
      if (entry.kind !== "item_kind" || isCurrentItemKind(entry.value)) chip.append(remove);
      coversRow.append(chip);
    }
    coversEmpty.hidden = draft.scope.length > 0;
  });

  // Category chips: item kinds (Beer, Wine) and the spirit categories, A to Z.
  const catGroup = el("div", "admin-special-group");
  catGroup.append(el("span", "admin-special-group-title", "Categories"));
  const catRow = el("div", "admin-special-chips");
  catRow.setAttribute("role", "group");
  catRow.setAttribute("aria-label", "Categories this discount covers");
  const offered: ScopeEntry[] = [
    ...options.kindOptions.filter((o) => isCurrentItemKind(o.value)).map((o) => ({ kind: "item_kind" as const, value: o.value })),
    ...options.categoryOptions.map((o) => ({ kind: "category" as const, value: o.value })),
  ];
  // Anything already covered that the lists no longer offer stays tappable
  // (retire never loses, the DSC11 rule for the old scope editor).
  for (const entry of draft.scope) {
    if (
      (entry.kind === "category" || (entry.kind === "item_kind" && isCurrentItemKind(entry.value))) &&
      !offered.some((o) => entryKey(o) === entryKey(entry))
    ) {
      offered.push({ kind: entry.kind, value: entry.value });
    }
  }
  // UX88 (DEC-339): kinds and spirit categories read as one list, A to Z.
  for (const entry of sortByName(offered, labelFor)) {
    const label = labelFor(entry);
    const chip = tid(el("button", "admin-special-chip", label), "admin-discount-special-category");
    chip.type = "button";
    chip.dataset["kind"] = entry.kind;
    chip.dataset["value"] = entry.value;
    chip.addEventListener("click", () => toggleEntry(entry));
    syncs.push(() => setPressed(chip, hasEntry(entry.kind, entry.value), label));
    catRow.append(chip);
  }
  catGroup.append(catRow);
  catGroup.append(
    el(
      "p",
      "admin-special-help",
      "A category covers every drink in it, including any added later.",
    ),
  );
  applies.append(catGroup);

  // Specific drinks and bottles, loaded only when asked for.
  const pickGroup = el("div", "admin-special-group");
  const pickButton = tid(
    el("button", "btn btn-secondary admin-special-pick", "+ Pick specific drinks"),
    "admin-discount-special-pick-drinks",
  );
  pickButton.type = "button";
  pickButton.setAttribute("aria-expanded", "false");
  const pickPanel = tid(el("div", "admin-special-picker"), "admin-discount-special-picker");
  pickPanel.hidden = true;
  pickGroup.append(
    pickButton,
    el(
      "p",
      "admin-special-help",
      "Use this to cover one drink without its whole category. A bottle also covers the cocktails made with it.",
    ),
    pickPanel,
  );
  applies.append(pickGroup);
  let pickLoaded = false;
  const renderPickPanel = (load: Extract<PickableLoad, { ok: true }>): void => {
    pickPanel.replaceChildren();
    const section = (
      title: string,
      kind: "menu_drink" | "liquor_product",
      rows: PickableNamed[],
    ): void => {
      pickPanel.append(el("span", "admin-special-group-title", title));
      const row = el("div", "admin-special-chips");
      row.setAttribute("role", "group");
      row.setAttribute("aria-label", title);
      if (rows.length === 0) {
        row.append(el("p", "admin-special-help", `No ${title.toLowerCase()} to pick.`));
      }
      for (const item of sortByName(rows, (listed) => listed.name)) {
        const chip = tid(
          el("button", "admin-special-chip", item.name),
          "admin-discount-special-picker-chip",
        );
        chip.type = "button";
        chip.dataset["kind"] = kind;
        chip.dataset["value"] = item.id;
        chip.addEventListener("click", () =>
          toggleEntry({ kind, value: item.id, name: item.name }),
        );
        syncs.push(() => setPressed(chip, hasEntry(kind, item.id), item.name));
        row.append(chip);
      }
      pickPanel.append(row);
    };
    section("Drinks", "menu_drink", load.drinks);
    section("Bottles", "liquor_product", load.bottles);
    syncAll();
  };
  pickButton.addEventListener("click", () => {
    const open = pickPanel.hidden;
    pickPanel.hidden = !open;
    pickButton.setAttribute("aria-expanded", String(open));
    pickButton.textContent = open ? "Done picking drinks" : "+ Pick specific drinks";
    if (!open || pickLoaded) {
      return;
    }
    pickLoaded = true;
    pickPanel.replaceChildren(el("p", "admin-special-help", "Loading drinks and bottles."));
    void options.loadPickable().then((load) => {
      if (!load.ok) {
        pickLoaded = false;
        pickPanel.replaceChildren(
          el("p", "admin-special-problem", `Could not load drinks. ${load.message}`),
        );
        return;
      }
      renderPickPanel(load);
    });
  });
  root.append(applies);

  // --- The note, then Save. ----------------------------------------------
  const note = tid(el("div", "admin-special-note"), "admin-discount-special-note");
  note.append(
    el("strong", undefined, "Applies itself during its window"),
    el(
      "span",
      undefined,
      " While the window is open, this takes the discount off every eligible drink as it is rung. Staff do not pick it.",
    ),
  );
  if (!SCHEDULED_SPECIALS_APPLY_ITSELF_LIVE) {
    note.hidden = true;
  }
  root.append(note);

  const footer = el("div", "admin-special-footer");
  const save = tid(
    el("button", "btn btn-primary admin-special-save", "Save schedule"),
    "admin-discount-special-save",
  );
  save.type = "button";
  const status = tid(el("p", "admin-special-status"), "admin-discount-special-status");
  status.setAttribute("role", "status");
  footer.append(save, status);
  root.append(footer);

  const showStatus = (message: string, kind: "problem" | "ok" | ""): void => {
    status.textContent = message;
    status.className = `admin-special-status${kind === "" ? "" : ` admin-special-${kind}`}`;
  };

  save.addEventListener("click", () => {
    void (async () => {
      if (draft.scheduled && zoneIsUnset(options.getZone())) {
        syncAll();
        showStatus(
          "Set the location's time zone before saving a schedule. It is still the default time zone.",
          "problem",
        );
        return;
      }
      const problem = draftProblem(draft);
      if (problem !== null) {
        showStatus(problem, "problem");
        return;
      }
      save.disabled = true;
      showStatus("Saving.", "");
      const result = await options.onSave(draftToPayload(draft));
      save.disabled = false;
      if (!root.isConnected) {
        return;
      }
      showStatus(result.ok ? "Saved." : result.message, result.ok ? "ok" : "problem");
    })();
  });

  // --- Switch wiring last, so every sync target exists. -------------------
  switchInput.addEventListener("change", () => {
    draft.scheduled = switchInput.checked;
    showStatus("", "");
    syncAll();
  });
  syncs.push(() => {
    switchInput.checked = draft.scheduled;
    scheduleBody.hidden = !draft.scheduled;
    applies.hidden = !draft.scheduled;
    note.hidden = !draft.scheduled || !SCHEDULED_SPECIALS_APPLY_ITSELF_LIVE;
  });
  syncAll();
  return root;
}

function setPressed(chip: HTMLButtonElement, pressed: boolean, label: string): void {
  chip.setAttribute("aria-pressed", String(pressed));
  chip.classList.toggle("admin-special-chip-on", pressed);
  chip.textContent = pressed ? `✓ ${label}` : label;
}

function makeSwitch(
  title: string,
  testid: string,
): { label: HTMLElement; input: HTMLInputElement } {
  const wrap = el("div", "admin-special-switch-row");
  const label = el("label", "admin-toggle-label admin-special-switch");
  const input = tid(el("input", "admin-toggle-input"), testid);
  input.type = "checkbox";
  input.setAttribute("role", "switch");
  input.setAttribute("aria-label", title);
  const text = el("span", "admin-special-switch-text");
  text.append(el("strong", undefined, title));
  label.append(input, el("span", "admin-toggle-track"), text);
  wrap.append(label);
  return { label: wrap, input };
}

function makeDate(title: string, testid: string): { wrap: HTMLElement; input: HTMLInputElement } {
  const wrap = el("label", "admin-special-field");
  wrap.append(el("span", "admin-special-group-title", title));
  const input = tid(el("input", "field-input"), testid);
  input.type = "date";
  input.setAttribute("aria-label", `${title} date`);
  wrap.append(input);
  return { wrap, input };
}

function makeTimeSelect(
  title: string,
  testid: string,
): { wrap: HTMLElement; select: HTMLSelectElement; setValue: (value: string) => void } {
  const wrap = el("label", "admin-special-field");
  wrap.append(el("span", "admin-special-group-title", title));
  const select = tid(el("select", "field-input"), testid);
  select.setAttribute("aria-label", `${title} time`);
  for (const choice of timeChoices()) {
    const option = el("option", undefined, timeLabel(choice));
    option.value = choice;
    select.append(option);
  }
  wrap.append(select);
  return {
    wrap,
    select,
    // A time stored off the 15-minute grid (set through the API) stays
    // visible and selectable rather than silently reading as another time.
    setValue: (value: string): void => {
      if (!Array.from(select.options).some((o) => o.value === value)) {
        const extra = el("option", undefined, formatClockTime(value));
        extra.value = value;
        select.append(extra);
      }
      select.value = value;
    },
  };
}
